import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';

/**
 * Reset Password page · `/reset-password?token=<raw>`.
 *
 * Token comes from the emailed link. Pure client form here — no auth.
 * On success, redirects to /login with a success banner.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const tokenFromUrl = params.get('token') || '';

  const [token, setToken] = useState(tokenFromUrl);
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const strength = useMemo(() => {
    if (!pwd) return null;
    if (pwd.length < 8) return { label: 'Too short', color: '#DC2626' };
    const hasLetter = /[A-Za-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^A-Za-z0-9]/.test(pwd);
    const score = [hasLetter, hasNumber, hasSymbol].filter(Boolean).length;
    if (score === 3) return { label: 'Strong', color: '#16653A' };
    if (score === 2) return { label: 'Good', color: '#2A7B88' };
    return { label: 'Weak', color: '#D97706' };
  }, [pwd]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pwd !== pwd2) {
      setErr('Passwords do not match');
      return;
    }
    if (pwd.length < 8) {
      setErr('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token: token.trim(), new_password: pwd });
      navigate('/login?reset=1');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message || 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#F7F5F2' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: '#1B2A4A' }}>Set a new password</h1>
          <p className="mt-1 text-sm" style={{ color: '#6B7280' }}>Choose something you'll remember but others can't guess.</p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="reset-password-form"
          className="bg-white rounded-lg shadow-sm border p-6 space-y-4"
          style={{ borderColor: '#E5E1DC' }}
        >
          {!tokenFromUrl && (
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>Reset token</label>
              <input
                data-testid="reset-token-input"
                type="text"
                value={token}
                onChange={e => setToken(e.target.value)}
                required
                className="w-full px-3 py-2.5 rounded-md border text-sm font-mono focus:outline-none"
                style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
                placeholder="Paste token from email"
              />
              <p className="text-[10px] mt-1" style={{ color: '#9CA3AF' }}>
                Auto-filled when you click the link in the email. Manual entry only if the link didn't carry across.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>New password</label>
            <input
              data-testid="reset-new-password-input"
              type="password"
              value={pwd}
              onChange={e => setPwd(e.target.value)}
              required
              minLength={8}
              autoFocus
              className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              placeholder="At least 8 characters"
            />
            {strength && (
              <p className="text-[11px] mt-1" style={{ color: strength.color }}>
                Strength: {strength.label}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1.5" style={{ color: '#1B2A4A' }}>Confirm new password</label>
            <input
              data-testid="reset-confirm-password-input"
              type="password"
              value={pwd2}
              onChange={e => setPwd2(e.target.value)}
              required
              minLength={8}
              className="w-full px-3 py-2.5 rounded-md border text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: '#E5E1DC', color: '#1B2A4A' }}
              placeholder="Re-enter the same password"
            />
          </div>

          {err && (
            <div className="text-sm py-2 px-3 rounded-md" style={{ backgroundColor: '#FEF2F2', color: '#D4726A' }} data-testid="reset-error">
              {err}
            </div>
          )}

          <button
            data-testid="reset-password-submit-btn"
            type="submit"
            disabled={submitting || !pwd || !pwd2}
            className="w-full py-2.5 rounded-md text-sm font-medium text-white transition-opacity disabled:opacity-50"
            style={{ backgroundColor: '#1B2A4A' }}
          >
            {submitting ? 'Updating…' : 'Update password'}
          </button>

          <div className="text-center pt-1">
            <Link to="/login" data-testid="reset-back-link" className="text-[12px] underline" style={{ color: '#6B7280' }}>
              Back to login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
